import { forwardRef } from 'react'
import { AlertCircle } from 'lucide-react'

interface SectionTopAlertProps {
  children: React.ReactNode
}

/**
 * Inline error banner for the TOP of a section card — it sits directly beneath
 * the section's grey helper text, above the first field.
 *
 * Spacing rule (locked): 24px above and 24px below. The grey helper text already
 * leaves 32px under itself, so the banner pulls up 8px to land on a 24px gap;
 * sections WITHOUT a top alert keep the full 32px helper gap untouched.
 *
 * Reuse this for every top-of-container error box so the spacing stays uniform.
 */
export const SectionTopAlert = forwardRef<HTMLDivElement, SectionTopAlertProps>(
  function SectionTopAlert({ children }, ref) {
    return (
      <div
        ref={ref}
        role="alert"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          background: 'var(--color-error-surface)',
          border: '1px solid var(--color-error-border)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--spacing-3) var(--spacing-4)',
          // Pull up 8px so the helper's 32px gap reads as 24px above; 24px below.
          marginTop: 'calc(-1 * var(--spacing-2))',
          marginBottom: 'var(--spacing-6)',
        }}
      >
        <AlertCircle
          size={16}
          color="var(--color-error-text)"
          strokeWidth={2}
          aria-hidden="true"
          style={{ flexShrink: 0 }}
        />
        <p
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-label-sm)',
            color: 'var(--color-error-text)',
            margin: 0,
            lineHeight: 'var(--leading-normal)',
          }}
        >
          {children}
        </p>
      </div>
    )
  }
)
